/**
 * GameCard Component
 * Universal card component for displaying games
 */

import React from "react";
import { View, Text, TouchableOpacity, StyleSheet } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { GameWithConfig, UserGameStats } from "../types";

interface GameCardProps {
  /** Game definition with config */
  game: GameWithConfig;
  /** User's stats for this game (optional) */
  stats?: UserGameStats;
  /** Card size variant */
  size?: "small" | "medium" | "large";
  /** Show weekend badge */
  showWeekendBadge?: boolean;
  /** Show stats */
  showStats?: boolean;
  /** Callback when card is pressed */
  onPress?: () => void;
  /** Additional style */
  style?: object;
}

/**
 * Game card component for the games list
 */
export const GameCard: React.FC<GameCardProps> = ({
  game,
  stats,
  size = "medium",
  showWeekendBadge = true,
  showStats = false,
  onPress,
  style,
}) => {
  const colors = useThemeColors();

  const sizeStyles = {
    small: {
      width: 100,
      padding: 12,
      iconSize: 24,
      iconBgSize: 40,
      titleSize: 14,
    },
    medium: {
      width: "48%" as const,
      padding: 16,
      iconSize: 28,
      iconBgSize: 48,
      titleSize: 16,
    },
    large: {
      width: "100%" as const,
      padding: 20,
      iconSize: 32,
      iconBgSize: 56,
      titleSize: 18,
    },
  };

  const currentSize = sizeStyles[size];

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={!onPress}
      activeOpacity={0.7}
      style={[
        styles.container,
        {
          width: currentSize.width,
          padding: currentSize.padding,
          backgroundColor: `${game.color}15`,
          borderWidth: 1,
          borderColor: `${game.color}30`,
        },
        style,
      ]}
      accessibilityRole="button"
      accessibilityLabel={`${game.name} spel openen`}
    >
      {/* Weekend Badge */}
      {showWeekendBadge && game.is_weekend_mode && (
        <View
          style={[
            styles.weekendBadge,
            { backgroundColor: game.color },
          ]}
        >
          <Ionicons name="trophy" size={10} color="#FFFFFF" />
          <Text style={styles.weekendBadgeText}>
            {game.effective_points_config.win || 
             game.effective_points_config.correct_answer || 
             "+"} pts
          </Text>
        </View>
      )}

      {/* Free Play Badge */}
      {showWeekendBadge && !game.is_weekend_mode && game.supports_free_play && (
        <View
          style={[
            styles.freePlayBadge,
            { backgroundColor: colors.textTertiary },
          ]}
        >
          <Text style={styles.freePlayBadgeText}>Vrij spel</Text>
        </View>
      )}

      {/* Icon */}
      <View
        style={[
          styles.iconContainer,
          {
            width: currentSize.iconBgSize,
            height: currentSize.iconBgSize,
            backgroundColor: `${game.color}30`,
          },
        ]}
      >
        <Ionicons
          name={game.icon as any}
          size={currentSize.iconSize}
          color={game.color}
        />
      </View>

      {/* Title */}
      <Text
        style={[
          styles.title,
          {
            fontSize: currentSize.titleSize,
            color: colors.text,
          },
        ]}
        numberOfLines={1}
      >
        {game.name}
      </Text>

      {/* Description (only for medium and large) */}
      {size !== "small" && game.description && (
        <Text
          style={[styles.description, { color: colors.textSecondary }]}
          numberOfLines={2}
        >
          {game.description}
        </Text>
      )}

      {/* Stats (if enabled and available) */}
      {showStats && stats && (
        <View style={styles.statsContainer}>
          <View style={styles.statItem}>
            <Ionicons name="game-controller" size={12} color={colors.textSecondary} />
            <Text style={[styles.statText, { color: colors.textSecondary }]}>
              {stats.games_played}
            </Text>
          </View>
          <View style={styles.statItem}>
            <Ionicons name="trophy" size={12} color="#F59E0B" />
            <Text style={[styles.statText, { color: colors.textSecondary }]}>
              {stats.games_won}
            </Text>
          </View>
          {stats.current_streak > 0 && (
            <View style={styles.statItem}>
              <Ionicons name="flame" size={12} color="#EF4444" />
              <Text style={[styles.statText, { color: colors.textSecondary }]}>
                {stats.current_streak}
              </Text>
            </View>
          )}
        </View>
      )}

      {/* Category indicator (only for large) */}
      {size === "large" && (
        <View style={styles.categoryContainer}>
          <Text
            style={[
              styles.category,
              {
                color: game.color,
                backgroundColor: `${game.color}20`,
              },
            ]}
          >
            {game.category}
          </Text>
        </View>
      )}
    </TouchableOpacity>
  );
};

/**
 * Compact game card for horizontal lists
 */
export const GameCardCompact: React.FC<
  Omit<GameCardProps, "size"> & { width?: number }
> = ({ game, stats, showWeekendBadge = true, onPress, width = 120, style }) => {
  const colors = useThemeColors();

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={!onPress}
      activeOpacity={0.7}
      style={[
        styles.compactContainer,
        {
          width,
          backgroundColor: `${game.color}15`,
          borderWidth: 1,
          borderColor: `${game.color}30`,
        },
        style,
      ]}
      accessibilityRole="button"
      accessibilityLabel={`${game.name} spel openen`}
    >
      {/* Weekend indicator */}
      {showWeekendBadge && game.is_weekend_mode && (
        <View
          style={[styles.compactBadge, { backgroundColor: game.color }]}
        >
          <Ionicons name="trophy" size={8} color="#FFFFFF" />
        </View>
      )}

      <View
        style={[
          styles.compactIconContainer,
          { backgroundColor: `${game.color}30` },
        ]}
      >
        <Ionicons name={game.icon as any} size={20} color={game.color} />
      </View>

      <Text
        style={[styles.compactTitle, { color: colors.text }]}
        numberOfLines={1}
      >
        {game.name}
      </Text>

      {stats && (
        <Text style={[styles.compactStats, { color: colors.textSecondary }]}>
          {stats.total_points_earned} pts
        </Text>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 16,
    marginBottom: 12,
    position: "relative",
  },
  weekendBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    gap: 2,
  },
  weekendBadgeText: {
    fontSize: 10,
    fontWeight: "700",
    color: "#FFFFFF",
  },
  freePlayBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  freePlayBadgeText: {
    fontSize: 10,
    fontWeight: "500",
    color: "#FFFFFF",
  },
  iconContainer: {
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 12,
  },
  title: {
    fontWeight: "700",
  },
  description: {
    fontSize: 13,
    marginTop: 4,
  },
  statsContainer: {
    flexDirection: "row",
    marginTop: 12,
    gap: 12,
  },
  statItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  statText: {
    fontSize: 12,
  },
  categoryContainer: {
    marginTop: 12,
  },
  category: {
    fontSize: 11,
    fontWeight: "600",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    alignSelf: "flex-start",
    textTransform: "capitalize",
  },
  // Compact styles
  compactContainer: {
    borderRadius: 12,
    padding: 12,
    alignItems: "center",
    marginRight: 12,
    position: "relative",
  },
  compactBadge: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 16,
    height: 16,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  compactIconContainer: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
  },
  compactTitle: {
    fontSize: 13,
    fontWeight: "600",
  },
  compactStats: {
    fontSize: 11,
    marginTop: 2,
  },
});

export default GameCard;

